import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { api, unwrap } from '../../api/client';
import { errorMessage } from '../../api/errors';
import type { components } from '../../api/schema';
import { useAuth } from '../../auth/AuthProvider';
import { Button, Field, Input, Modal, Select } from '../../design';
import { useToast } from '../../design/Toast';
import { useOrgOptions } from '../users/UsersPage';
import { TYPE_LABEL, type LockerType } from './common';
import { LayoutGrid } from './LayoutGrid';
import type { Slot } from './layout';
import { firstFreeCell } from './layout';
import s from './lockers.module.css';

type LockerCreate = components['schemas']['LockerCreate'];

const STEPS = ['Identitate', 'Controler', 'Plăci', 'Grilă', 'Locație'];
const DEFAULT_CONTROL = { x: 1, y: 0, w: 1, h: 2 };

/** Wizard-ul din mockup, 5 pași, un singur `POST /lockers`; compartimentele se generează 1:1 pe ieșiri. */
export function LockerWizard({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: string) => void;
}) {
  const { user } = useAuth();
  const orgs = useOrgOptions();
  const qc = useQueryClient();
  const toast = useToast();
  const [step, setStep] = useState(0);
  const form = useForm<LockerCreate>({
    defaultValues: {
      orgId: user?.orgId ?? '',
      name: '',
      type: 'office',
      serialNumber: '',
      simIccid: null,
      comment: '',
      controller: {
        deviceLabel: 'Tabletă RK3288',
        os: 'Android 5.1.1',
        screenInches: 21,
        resolution: { width: 1080, height: 1920 },
        orientation: 'portrait',
        touch: true,
        appVersion: '',
        serial: {
          port: '/dev/ttyS4',
          protocol: 'wondware-v1',
          baud: 9600,
          dataBits: 8,
          parity: 'none',
          stopBits: 1,
          timeoutMs: 800,
          retries: 3,
          pollMs: 1500,
        },
      },
      boards: [{ name: 'Placa 01', address: 1, doors: 24, firmware: '' }],
      cols: 4,
      location: { city: '', address: '', lat: 44.43, lng: 26.1 },
    },
  });
  const create = useMutation({
    mutationFn: (d: LockerCreate) =>
      unwrap(
        api.POST('/lockers', {
          body: {
            ...d,
            simIccid: d.simIccid ? d.simIccid : null,
            controller: {
              ...d.controller,
              screenInches: Number(d.controller.screenInches),
              resolution: {
                width: Number(d.controller.resolution.width),
                height: Number(d.controller.resolution.height),
              },
              serial: {
                ...d.controller.serial,
                baud: Number(
                  d.controller.serial.baud,
                ) as LockerCreate['controller']['serial']['baud'],
                dataBits: Number(d.controller.serial.dataBits) as 7 | 8,
                stopBits: Number(d.controller.serial.stopBits) as 1 | 2,
                timeoutMs: Number(d.controller.serial.timeoutMs),
                retries: Number(d.controller.serial.retries),
                pollMs: Number(d.controller.serial.pollMs),
              },
            },
            boards: d.boards.map((b) => ({
              ...b,
              address: Number(b.address),
              doors: Number(b.doors),
            })),
            cols: Number(d.cols),
            location: { ...d.location, lat: Number(d.location.lat), lng: Number(d.location.lng) },
          },
        }),
      ),
    onSuccess: (l) => {
      toast(
        `Lockerul ${l.name} a fost creat. Codul de înrolare este în tab-ul Controler & plăci.`,
        'success',
      );
      void qc.invalidateQueries({ queryKey: ['lockers'] });
      onCreated(l.id);
    },
    onError: (e) => toast(errorMessage(e), 'error'),
  });

  const boards = useWatch({ control: form.control, name: 'boards' });
  const cols = Number(useWatch({ control: form.control, name: 'cols' })) || 4;
  const errors = form.formState.errors;

  const fieldsOfStep: (keyof LockerCreate)[][] = [
    ['orgId', 'name', 'type', 'serialNumber', 'simIccid'],
    ['controller'],
    ['boards'],
    ['cols'],
    ['location'],
  ];
  const next = async () => {
    const ok = await form.trigger(fieldsOfStep[step] as never);
    if (ok) setStep((x) => Math.min(STEPS.length - 1, x + 1));
  };

  // Previzualizare: sloturile 1:1 pe ieșiri, împachetate în jurul ecranului.
  const previewSlots: Slot[] = [];
  {
    const occupied = new Set<string>();
    for (let y = DEFAULT_CONTROL.y; y < DEFAULT_CONTROL.y + DEFAULT_CONTROL.h; y++)
      for (let x = DEFAULT_CONTROL.x; x < DEFAULT_CONTROL.x + DEFAULT_CONTROL.w; x++)
        occupied.add(`${x},${y}`);
    let n = 0;
    for (const b of boards) {
      for (let d = 1; d <= Math.min(64, Number(b.doors) || 0); d++) {
        const cell = firstFreeCell(cols, occupied);
        if (!cell) break;
        occupied.add(`${cell.x},${cell.y}`);
        n++;
        previewSlots.push({
          id: `p-${n}`,
          lockerId: '',
          label: String(n).padStart(2, '0'),
          size: 'M',
          boardId: String(b.address),
          door: d,
          rect: cell,
          dimensionsMm: { width: 350, height: 320, depth: 450 },
          sensor: 'NC',
          techState: 'active',
          occupancy: 'free',
          doorOpen: false,
          lastEventAt: null,
        });
      }
    }
  }

  return (
    <Modal
      title="Locker nou"
      onClose={onClose}
      wide
      footer={
        <>
          <Button onClick={onClose}>Renunță</Button>
          {step > 0 && <Button onClick={() => setStep((x) => x - 1)}>Înapoi</Button>}
          {step < STEPS.length - 1 ? (
            <Button variant="primary" onClick={() => void next()}>
              Continuă
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={form.handleSubmit((d) => create.mutate(d))}
              busy={create.isPending}
            >
              Creează lockerul
            </Button>
          )}
        </>
      }
    >
      <div className={s.stepper}>
        {STEPS.map((label, i) => (
          <div
            key={label}
            className={[s.step, i === step && s.stepActive, i < step && s.stepDone]
              .filter(Boolean)
              .join(' ')}
          >
            <i>{i < step ? '✓' : i + 1}</i>
            {label}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="stack">
          <div className={s.formGrid}>
            <Field label="Organizație" error={errors.orgId?.message} htmlFor="w-org">
              <Select
                id="w-org"
                disabled={user?.orgId !== null}
                invalid={!!errors.orgId}
                {...form.register('orgId', { required: 'Alege organizația' })}
              >
                <option value="">Alege…</option>
                {orgs.data?.items.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Tip" htmlFor="w-type">
              <Select id="w-type" {...form.register('type')}>
                {(Object.keys(TYPE_LABEL) as LockerType[]).map((t) => (
                  <option key={t} value={t}>
                    {TYPE_LABEL[t]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Nume" error={errors.name?.message} htmlFor="w-name">
              <Input
                id="w-name"
                invalid={!!errors.name}
                {...form.register('name', {
                  required: 'Numele este obligatoriu',
                  minLength: { value: 2, message: 'Minimum 2 caractere' },
                })}
              />
            </Field>
            <Field label="Serie" error={errors.serialNumber?.message} htmlFor="w-sn">
              <Input
                id="w-sn"
                invalid={!!errors.serialNumber}
                {...form.register('serialNumber', {
                  required: 'Seria este obligatorie',
                  minLength: { value: 3, message: 'Minimum 3 caractere' },
                })}
              />
            </Field>
            <Field
              label="ICCID SIM"
              hint="18–22 cifre, opțional"
              error={errors.simIccid?.message}
              htmlFor="w-sim"
            >
              <Input
                id="w-sim"
                invalid={!!errors.simIccid}
                {...form.register('simIccid', {
                  pattern: { value: /^([0-9]{18,22})?$/, message: '18–22 cifre' },
                })}
              />
            </Field>
          </div>
          <Field label="Comentarii" htmlFor="w-comment">
            <Input id="w-comment" {...form.register('comment')} />
          </Field>
        </div>
      )}

      {step === 1 && (
        <div className="stack">
          <div className={s.formGrid}>
            <Field label="Dispozitiv" htmlFor="w-dev">
              <Input id="w-dev" {...form.register('controller.deviceLabel', { required: true })} />
            </Field>
            <Field label="Sistem de operare" htmlFor="w-os">
              <Input id="w-os" {...form.register('controller.os')} />
            </Field>
            <Field label="Ecran (inch)" htmlFor="w-inch">
              <Input
                id="w-inch"
                type="number"
                step="0.1"
                {...form.register('controller.screenInches')}
              />
            </Field>
            <Field label="Orientare" htmlFor="w-orient">
              <Select id="w-orient" {...form.register('controller.orientation')}>
                <option value="portrait">Portret</option>
                <option value="landscape">Peisaj</option>
              </Select>
            </Field>
            <Field label="Rezoluție lățime" htmlFor="w-rw">
              <Input id="w-rw" type="number" {...form.register('controller.resolution.width')} />
            </Field>
            <Field label="Rezoluție înălțime" htmlFor="w-rh">
              <Input id="w-rh" type="number" {...form.register('controller.resolution.height')} />
            </Field>
          </div>
          <h3>Magistrala serială</h3>
          <div className={s.formGrid}>
            <Field
              label="Port"
              hint="/dev/ttyS4 pe tabletă; tcp://ip:port pentru punte"
              htmlFor="w-port"
            >
              <Input id="w-port" {...form.register('controller.serial.port', { required: true })} />
            </Field>
            <Field label="Protocol" htmlFor="w-proto">
              <Select id="w-proto" {...form.register('controller.serial.protocol')}>
                <option value="wondware-v1">Wondware v1</option>
              </Select>
            </Field>
            <Field label="Baud" htmlFor="w-baud">
              <Select id="w-baud" {...form.register('controller.serial.baud')}>
                {[2400, 4800, 9600, 19200, 38400, 57600, 115200].map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Biți de date" htmlFor="w-bits">
              <Select id="w-bits" {...form.register('controller.serial.dataBits')}>
                <option value={8}>8</option>
                <option value={7}>7</option>
              </Select>
            </Field>
            <Field label="Paritate" htmlFor="w-par">
              <Select id="w-par" {...form.register('controller.serial.parity')}>
                <option value="none">fără</option>
                <option value="even">pară</option>
                <option value="odd">impară</option>
              </Select>
            </Field>
            <Field label="Biți de stop" htmlFor="w-stop">
              <Select id="w-stop" {...form.register('controller.serial.stopBits')}>
                <option value={1}>1</option>
                <option value={2}>2</option>
              </Select>
            </Field>
            <Field label="Timeout (ms)" htmlFor="w-to">
              <Input id="w-to" type="number" {...form.register('controller.serial.timeoutMs')} />
            </Field>
            <Field label="Reîncercări" htmlFor="w-retry">
              <Input id="w-retry" type="number" {...form.register('controller.serial.retries')} />
            </Field>
            <Field label="Polling (ms)" htmlFor="w-poll">
              <Input id="w-poll" type="number" {...form.register('controller.serial.pollMs')} />
            </Field>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="stack">
          <p className="muted small">
            Fiecare placă are o adresă unică pe locker și un număr de ieșiri (relee).
            Compartimentele se generează câte unul pe ieșire.
          </p>
          <table
            className={`${s.boardsTable}`}
            style={{ width: '100%', borderCollapse: 'collapse' }}
          >
            <thead>
              <tr className="muted small">
                <th style={{ textAlign: 'left' }}>Nume</th>
                <th style={{ textAlign: 'left' }}>Adresă</th>
                <th style={{ textAlign: 'left' }}>Ieșiri</th>
                <th style={{ textAlign: 'left' }}>Firmware</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {boards.map((_, i) => (
                <tr key={i}>
                  <td>
                    <Input
                      aria-label={`Nume placă ${i + 1}`}
                      {...form.register(`boards.${i}.name`, { required: true })}
                    />
                  </td>
                  <td style={{ width: 110 }}>
                    <Input
                      aria-label={`Adresă placă ${i + 1}`}
                      type="number"
                      min={1}
                      max={255}
                      {...form.register(`boards.${i}.address`, { required: true })}
                    />
                  </td>
                  <td style={{ width: 110 }}>
                    <Input
                      aria-label={`Ieșiri placă ${i + 1}`}
                      type="number"
                      min={1}
                      max={64}
                      {...form.register(`boards.${i}.doors`, { required: true })}
                    />
                  </td>
                  <td style={{ width: 130 }}>
                    <Input
                      aria-label={`Firmware placă ${i + 1}`}
                      {...form.register(`boards.${i}.firmware`)}
                    />
                  </td>
                  <td style={{ width: 40 }}>
                    {boards.length > 1 && (
                      <Button
                        small
                        variant="ghost"
                        icon="trash"
                        aria-label={`Elimină placa ${i + 1}`}
                        onClick={() =>
                          form.setValue(
                            'boards',
                            boards.filter((__, j) => j !== i),
                          )
                        }
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div>
            <Button
              small
              icon="plus"
              onClick={() =>
                form.setValue('boards', [
                  ...boards,
                  {
                    name: `Placa ${String(boards.length + 1).padStart(2, '0')}`,
                    address: boards.length + 1,
                    doors: 12,
                    firmware: '',
                  },
                ])
              }
              disabled={boards.length >= 32}
            >
              Adaugă placă
            </Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="stack">
          <div className="flex">
            <Field label="Coloane (3–8)" htmlFor="w-cols">
              <Input
                id="w-cols"
                type="number"
                min={3}
                max={8}
                style={{ width: 120 }}
                {...form.register('cols', { min: 3, max: 8 })}
              />
            </Field>
            <span className="muted small">
              Ecranul ocupă coloana 2, rândurile 1–2; {previewSlots.length} compartimente pe{' '}
              {boards.reduce((a, b) => a + (Number(b.doors) || 0), 0)} ieșiri.
            </span>
          </div>
          <div className={s.gridWrap}>
            <LayoutGrid cols={cols} control={DEFAULT_CONTROL} slots={previewSlots} compact />
          </div>
        </div>
      )}

      {step === 4 && (
        <div className={s.formGrid}>
          <Field label="Oraș" error={errors.location?.city?.message} htmlFor="w-city">
            <Input
              id="w-city"
              invalid={!!errors.location?.city}
              {...form.register('location.city', {
                required: 'Orașul este obligatoriu',
                minLength: { value: 2, message: 'Minimum 2 caractere' },
              })}
            />
          </Field>
          <Field label="Adresă" htmlFor="w-addr">
            <Input id="w-addr" {...form.register('location.address')} />
          </Field>
          <Field label="Latitudine" htmlFor="w-lat">
            <Input
              id="w-lat"
              type="number"
              step="0.000001"
              {...form.register('location.lat', { min: -90, max: 90 })}
            />
          </Field>
          <Field label="Longitudine" htmlFor="w-lng">
            <Input
              id="w-lng"
              type="number"
              step="0.000001"
              {...form.register('location.lng', { min: -180, max: 180 })}
            />
          </Field>
        </div>
      )}
    </Modal>
  );
}
